// Reading the quantities and prices off a confirmation without losing their units.
//
// These forms state the same trade twice, once per unit, and the two are NOT
// interchangeable:
//
//   Quantity:        BUSHELS: 33,266.14  |  METRIC TONNES: 845
//   Price:           PER BUSHEL: $2.40   |  PER METRIC TONNE: $94.48
//   Board Price:     $4.645 CZ26 ($182.86/MT)
//   Basis:           PER METRIC TONNE: $94.48
//   Net Price:       PER METRIC TONNE: $277.34
//
// Taking the first number out of each block mixes a per-bushel board price with a
// per-tonne basis and adds them, which is what made an 845 MT contract come out
// nonsense. Every number is therefore kept with the unit it was quoted in, one unit is
// chosen for the contract, and only readings in that unit are used.
//
// The two forms also disagree about what "Price" means. One writes
// "PER BUSHEL: $4.22 + $1.55 = $5.77" - board, basis and net in one expression - while
// the other writes "PER BUSHEL: $2.40", which is the basis on its own and duplicates the
// Basis field. So a lone number under Price is never read as the net price; the explicit
// Board Price, Basis and Net Price fields win, and the expression form is only trusted
// when it actually contains "a + b = c".
import { Unit } from '../../../../Generated/Commodity-Management-Api';

export interface UnitReading {
    /** Undefined when the confirmation quoted a bare number with no unit. */
    unit?: Unit;
    value: number;
}

/** Unit names as these forms spell them. */
const UNIT_WORDS: { pattern: RegExp; unit: Unit }[] = [
    { pattern: /METRIC\s*TONNES?|\bMT\b|\/MT\b/i, unit: Unit.Tonne },
    { pattern: /BUSHELS?|\bBU\b/i, unit: Unit.Bu },
    { pattern: /SHORT\s*TONS?|\bTONS?\b/i, unit: Unit.Ton },
    { pattern: /POUNDS?|\bLBS?\b/i, unit: Unit.Lb },
    { pattern: /\bCWT\b/i, unit: Unit.Cwt },
    { pattern: /\bKG\b|KILOGRAMS?/i, unit: Unit.Kg },
];

function toNumber(text: string): number | undefined {
    const cleaned = text.replace(/[$,\s]/g, '');
    if (!cleaned) return undefined;
    const value = Number(cleaned);
    return isNaN(value) ? undefined : value;
}

/** The unit named in a fragment, if any. */
function unitIn(fragment: string): Unit | undefined {
    for (const { pattern, unit } of UNIT_WORDS) {
        if (pattern.test(fragment)) return unit;
    }
    return undefined;
}

/**
 * Every number in a block, each tagged with the unit governing it.
 *
 * A block reads as a series of segments: a unit word applies to the numbers following it
 * until the next unit word. "PER BUSHEL: $2.40 PER METRIC TONNE: $94.48" is therefore
 * 2.40/bu and 94.48/tonne, and "$4.645 CZ26 ($182.86/MT)" is a bare 4.645 plus
 * 182.86/tonne.
 */
export function readUnitReadings(block: string | undefined): UnitReading[] {
    if (!block) return [];

    const readings: UnitReading[] = [];

    // A contract month code like "CZ26" carries a number that is not a price.
    let remaining = block.replace(/\b[A-Z]{1,2}[FGHJKMNQUVXZ]\d{2}\b/gi, ' ');

    // First the trailing form, where the unit follows its number: "$182.86/MT", "845 MT".
    // Restricted to the abbreviations, and to a slash or a single space, so that
    // "BUSHELS: 33,266.14 METRIC TONNES: 845" does not read 33,266.14 as tonnes.
    const trailingUnit = /(\d[\d,]*(?:\.\d+)?)\s*(?:\/\s*(MT|BU|LB|LBS|CWT|KG|TON|TONNE)|\s(MT|BU|LB|LBS|CWT|KG))\b/gi;
    remaining = remaining.replace(trailingUnit, (_match, number: string, slashUnit: string, spacedUnit: string) => {
        const value = toNumber(number);
        const unit = unitIn(slashUnit ?? spacedUnit ?? '');
        if (value !== undefined && unit) readings.push({ unit, value });
        // Blanked so the pass below does not read it a second time.
        return ' ';
    });

    // Then the leading form, where a unit word governs the numbers that follow it until
    // the next unit word: "PER BUSHEL: $2.40 PER METRIC TONNE: $94.48".
    const unitWordPattern = /(METRIC\s*TONNES?|BUSHELS?|SHORT\s*TONS?|\bTONS?\b|POUNDS?|\bLBS?\b|\bCWT\b|KILOGRAMS?|\bKG\b|\bMT\b|\bBU\b)/i;
    const pieces = remaining.split(new RegExp(`(?=${unitWordPattern.source})`, 'i'));

    for (const piece of pieces) {
        const unit = unitIn(piece);

        const numbers = piece.match(/-?\d[\d,]*(?:\.\d+)?/g) ?? [];
        for (const raw of numbers) {
            const value = toNumber(raw);
            if (value !== undefined) readings.push({ unit, value });
        }
    }

    return readings;
}

/** The reading quoted in the wanted unit, if the confirmation gave one. */
export function readingIn(readings: readonly UnitReading[], unit: Unit | undefined): number | undefined {
    if (!unit) return undefined;
    return readings.find(reading => reading.unit === unit)?.value;
}

/** A number the confirmation quoted with no unit at all. */
export function bareReading(readings: readonly UnitReading[]): number | undefined {
    return readings.find(reading => reading.unit === undefined)?.value;
}

/**
 * The unit the contract is written in.
 *
 * "Quantity Priced" names it when present - that is the line saying what was actually
 * transacted ("METRIC TONNES: 845 MT"). Otherwise the quantity block decides, and a
 * confirmation quoting only bushels is a bushel contract.
 */
export function chooseContractUnit(
    quantityReadings: readonly UnitReading[],
    quantityPricedReadings: readonly UnitReading[]
): Unit | undefined {
    const priced = quantityPricedReadings.find(reading => reading.unit && reading.value > 0);
    if (priced?.unit) return priced.unit;

    const quantity = quantityReadings.find(reading => reading.unit && reading.value > 0);
    return quantity?.unit;
}

export interface ResolvedPrice {
    value?: number;
    /** The unit the value is quoted in. Undefined for a bare number - the caller decides. */
    unit?: Unit;
    /** True when the number came from a bare quote and no unit was stated. */
    assumedUnit: boolean;
}

/**
 * The value for a block, with the unit it was quoted in.
 *
 * A reading in the contract's unit wins. Failing that, a reading quoted in some OTHER
 * unit is returned with that unit - it is not converted here, because that needs the
 * commodity's bushel weight, which this module does not have; the plan converts it.
 * Last, a bare number is returned with no unit at all. Whether a bare number can be
 * trusted to be in the contract's unit depends on the rest of the document, so that
 * decision is the parser's, not this function's.
 */
export function resolvePrice(readings: readonly UnitReading[], contractUnit: Unit | undefined): ResolvedPrice {
    const exact = readingIn(readings, contractUnit);
    if (exact !== undefined) return { value: exact, unit: contractUnit, assumedUnit: false };

    const other = readings.find(reading => reading.unit !== undefined);
    if (other) return { value: other.value, unit: other.unit, assumedUnit: false };

    const bare = bareReading(readings);
    if (bare !== undefined) return { value: bare, unit: undefined, assumedUnit: true };

    return { assumedUnit: false };
}

/** Every unit named in a fragment, whether or not a number follows it. */
export function unitsNamedIn(fragment: string | undefined): Unit[] {
    if (!fragment) return [];
    const units: Unit[] = [];
    for (const { pattern, unit } of UNIT_WORDS) {
        if (pattern.test(fragment) && !units.includes(unit)) units.push(unit);
    }
    return units;
}

/**
 * The one unit a confirmation is written in, if it only ever names one.
 *
 * A confirmation that quotes bushels in one place and tonnes in another gives no way to
 * tell what a bare "$4.6525" is per - and on those forms the board price is routinely
 * per bushel while the basis is per tonne. So a bare number is only given a unit when
 * every unit word in the document agrees; otherwise it is left unknown for the user.
 *
 * Unit WORDS are counted, not just numbers with units: a form with a "BUSHELS:" column
 * left blank next to "METRIC TONNES: 845" is still a two-unit form, and its bare board
 * price is still a per-bushel futures quote.
 */
export function soleDocumentUnit(...blocks: readonly (string | undefined)[]): Unit | undefined {
    const units = new Set<Unit>();
    for (const block of blocks) {
        for (const unit of unitsNamedIn(block)) units.add(unit);
    }
    return units.size === 1 ? Array.from(units)[0] : undefined;
}
